<template>
  <div>
    <b-button
      v-if="canEdit"
      variant="primary"
      class="mb-1"
      :to="`/${$route.params.accountId}/catalogs/${catalogId}/sets/create`"
      size="sm"
    >
      <GjIcon
        class="mr-50"
        name="Plus"
      />
      {{ $tk('ClientPortal.CatalogSet.CreateCatalogSet') }}
    </b-button>
    <b-card
      no-body
      class="client-table--sets"
    >
      <b-skeleton-table
        v-if="loading"
        :rows="5"
        :columns="fields.length"
      />
      <b-table
        v-if="!loading"
        :items="items"
        :fields="fields"
        class="client-table__data catalog-set-table"
        show-empty
        :empty-text="$tk('ClientPortal.Catalogs.NoCatalogSetsHaveBeenCreatedYet')"
        :current-page="currentPage"
        :per-page="perPage"
        responsive
        :tbody-tr-class="newRecordRowStyle"
      >
        <template #empty="scope">
          <div class="text-center p-3 client-table__empty">
            <div class="empty__client-table">
              <div class="catalog-wrapper">
                <GjIcon
                  size="64px"
                  name="Book"
                />
                <div class="mb-50">
                  {{ scope.emptyText }}
                </div>
                <b-link
                  v-if="canEdit"
                  :to="`/${$route.params.accountId}/catalogs/${catalogId}/sets/create`"
                >
                  <GjIcon
                    size="14"
                    name="Plus"
                  />
                  <span class="create-text">{{ $tk('ClientPortal.CatalogSet.CreateCatalogSet') }}</span>
                </b-link>
              </div>
            </div>
          </div>
        </template>
        <template #cell(name)="data">
          <div class="d-flex align-items-center justify-content-between">
            <b-link
              :to="canEdit ?
                `/${$route.params.accountId}/catalogs/${data.item.catalogEncodedId}/sets/${data.item.encodedId}/edit`
                : `/${$route.params.accountId}/catalogs/${data.item.catalogEncodedId}/sets/${data.item.encodedId}/items` "
            >
              {{ data.item.name }}
            </b-link>
            <div>
              <b-button
                class="text-secondary mr-1 font-weight-normal"
                variant="link"
                :to="`/${$route.params.accountId}/catalogs/${data.item.catalogEncodedId}/sets/${data.item.encodedId}/items`"
              >
                <GjIcon
                  name="Media"
                  size="22"
                  class="mr-25"
                />
                {{ $tk('ClientPortal.Form.Items') }}
              </b-button>
              <b-button
                v-if="canEdit"
                class="text-secondary mr-1 font-weight-normal"
                variant="link"
                :to="`/${$route.params.accountId}/catalogs/${data.item.catalogEncodedId}/sets/${data.item.encodedId}/edit`"
              >
                <GjIcon
                  name="Edit"
                  size="22"
                  class="mr-25"
                />
                {{ $tk('ClientPortal.Form.Edit') }}
              </b-button>
              <b-button
                v-if="canEdit"
                class="text-secondary font-weight-normal"
                variant="link"
                @click="showDeleteConfirmModal(data.item)"
              >
                <GjIcon
                  name="Delete"
                  size="22"
                  class="mr-25"
                />
                {{ $tk('ClientPortal.Form.Delete') }}
              </b-button>
            </div>
          </div>
        </template>
      </b-table>
      <b-card-footer
        v-if="items.length > 0"
        class="client-table__footer d-flex"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
          class="my-0 mr-1"
          hide-goto-end-buttons
        >
          <template #prev-text>
            <GjIcon
              class="mr-25"
              name="ArrowLeft"
            />
          </template>
          <template #next-text>
            <GjIcon
              class="ml-25"
              name="ArrowRight"
            />
          </template>
        </b-pagination>
        <span>
          {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }} {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }} {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }} {{ $tk('ClientPortal.Pagination.Items') }}
        </span>
      </b-card-footer>
    </b-card>
    <b-modal
      ref="deleteModal"
      :title="$tk('ClientPortal.Catalogs.AreYouSureYouWantToDeleteThisCatalogSet')"
      centered
    >
      <span>{{ $tk('ClientPortal.Catalogs.Name') }}: <b>{{ deleteConfirmModel.catalogSet.name }}</b></span>
      <template
        v-slot:modal-footer="{ cancel }"
      >
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="deleteConfirmModel.loading"
          size="sm"
          @click="deleteCatalogSet(deleteConfirmModel.catalogSet)"
        >
          {{ $tk('ClientPortal.Form.Actions.Delete') }}
          <b-spinner
            v-if="deleteConfirmModel.loading"
            class="ml-50"
            variant="light"
            label="Spinning"
            small
          />
        </b-button></template>
    </b-modal>
  </div>
</template>

<script>
import notification from '@/mixins/toastification'
import CatalogSetsService from '@/api/client/catalogSets.service'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import { computed, ref } from '@vue/composition-api'
import store from '@/store'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    catalogId: String,
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      items: [],
      currentPage: 1,
      perPage: 10,
      loading: false,
      deleteConfirmModel: {
        catalogSet: {
          name: '',
        },
        loading: false,
      },
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    totalRows() {
      return this.items.length
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$tk('ClientPortal.Catalogs.Name'),
        },
      ]
    },
  },
  async mounted() {
    this.loading = true
    try {
      this.items = await CatalogSetsService.getCatalogSets(this.catalogId, this.$route.params.accountId)
    } catch (err) {
      this.error(err.message)
    } finally {
      this.loading = false
    }
  },
  methods: {
    showDeleteConfirmModal(catalogSet) {
      this.deleteConfirmModel.catalogSet = catalogSet
      this.$refs.deleteModal.show()
    },
    async deleteCatalogSet(catalogSet) {
      try {
        this.deleteConfirmModel.loading = true
        const response = await CatalogSetsService.deleteCatalogSet(catalogSet.catalogEncodedId, catalogSet.encodedId, this.$route.params.accountId)
        if (response.saved) {
          this.items = this.items.filter(x => x.encodedId !== catalogSet.encodedId)
          this.success(response.message)
        } else {
          this.error(response.message)
        }
      } catch (err) {
        this.error(err.message)
      } finally {
        this.deleteConfirmModel.loading = false
        this.$refs.deleteModal.hide()
      }
    },
  },
  setup() {
    const isCreatedNew = computed(() => store.state.app.isCreatedNew)
    const alreadySet = ref(false)
    const firstRow = ref(null)
    const newRecordRowStyle = () => {
      if (!alreadySet.value) {
        firstRow.value = Array.from(document.querySelectorAll('.catalog-set-table > table > tbody > tr:first-child')).pop()
        if (firstRow.value) {
          if (isCreatedNew.value) {
            firstRow.value.classList.add('table-row-animation')
          } else if (firstRow.value.classList.contains('table-row-animation')) {
            firstRow.value.classList.remove('table-row-animation')
          }
          alreadySet.value = true
        }
      }
      return ''
    }

    if (isCreatedNew.value) {
      setTimeout(() => {
        store.commit('app/TOGGLE_NEW_CREATE_STATE', false)
        firstRow.value.classList.remove('table-row-animation')
      }, 3000)
    }
    return {
      isCreatedNew,
      newRecordRowStyle,
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
</style>
